import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSearch, FaEdit, FaPaperclip } from "react-icons/fa";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import DataTable from "react-data-table-component";
import "../common/style.css";
import "./manageBatch.css";
import { getBatchesByProject } from "../http/api";
import Loading from "../loading/loading";
import { useAuth } from "../http/authcontex";

// simple debounce util
function debounce(fn, delay) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

export default function ShowProjectBatches() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // DataTable pages are 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const authContext = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  // Helper to extract rows & total reliably from various API shapes
  const extractDataAndTotal = (response) => {
    const data = response.data;
    if (Array.isArray(data)) {
      // Fallback
      return {
        items: data,
        total: response.headers?.["x-total-count"]
          ? Number(response.headers["x-total-count"])
          : data.length,
      };
    }

    // ✅ Spring page format
    if (data && data.content) {
      return {
        items: data.content,
        total: Number(data.totalElements ?? data.total ?? 0),
      };
    }

    // fallback
    return { items: [], total: 0 };
  };

  // Fetch function
  const fetchPage = useCallback(
    (pageNumber = 1, size = rowsPerPage, search = searchTerm) => {
      // pageNumber is 1-based in UI; backend usually 0-based
      setLoading(true);
      getBatchesByProject(projectId, pageNumber - 1, size, search)
        .then((response) => {
          if (response.status === 200) {
            const { items, total } = extractDataAndTotal(response);
            setBatches(items || []);
            setTotalRows(Number(total || 0));
          } else {
            console.warn("Unexpected status", response.status, response);
          }
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          console.error("Error fetching batches:", error);
          if (error?.response?.status === 401) {
            authContext.logout();
            navigate("/");
          }
        });
    },
    [projectId, rowsPerPage, searchTerm, authContext, navigate]
  );

  // Initial + whenever page / rowsPerPage change
  useEffect(() => {
    fetchPage(page, rowsPerPage, searchTerm);
  }, [fetchPage, page, searchTerm, rowsPerPage]);

  // Debounced search: trigger server request after user stops typing
  useEffect(() => {
    const debounced = debounce((val) => {
      setPage(1); // reset to first page for new search
      fetchPage(1, rowsPerPage, val);
    }, 350);

    debounced(searchTerm);
    // cleanup not strictly necessary for this debounce, but okay
  }, [searchTerm, rowsPerPage, fetchPage]);

  // DataTable handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchPage(newPage, rowsPerPage, searchTerm);
    // fetchPage will be called by useEffect
  };

  const handlePerRowsChange = (newPerPage, currentPage) => {
    setRowsPerPage(newPerPage);
    fetchPage(1, newPerPage, searchTerm);
    // setPage(1); // optional: reset to page 1; DataTable passes currentPage too
    // fetchPage(currentPage, newPerPage); // useEffect will handle fetching
  };

  // keep client-side search disabled (server-side search). If you prefer client-side, remove paginationServer and just set data=batches filtered locally.
  const handleSearchInput = (e) => setSearchTerm(e.target.value);

  const handleEdit = (batch) => navigate(`/editBatch/${projectId}/${batch.id}`);
  const handleAddEvidence = (batch) => navigate(`/evidence/batch/${batch.id}`);
  const handleAddBatch = () => navigate(`/addBatch/${projectId}`);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-GB");
  };

  const columns = [
    {
      name: "Actions",
      minWidth: "180px",
      cell: (row) => (
        <div className="btn-group">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleEdit(row)}
          >
            <FaEdit /> Edit
          </button>
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleAddEvidence(row)}
          >
            <FaPaperclip className="me-1" /> Evidence
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Batch ID",
      selector: (row) => row.batchId,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Location",
      selector: (row) => row.location,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Job Role",
      selector: (row) => row.jobRole,
      sortable: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: "Start Date",
      selector: (row) => formatDate(row.startDate),
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "End Date",
      selector: (row) => formatDate(row.endDate),
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      minWidth: "150px",
    },
  ];

  return (
    <div className="ShowBatches">
      {loading && <Loading />}
      <div
        className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
      >
        <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav">
          <SideNave isSidebarOpen={isSidebarOpen} />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">Batches</h4>
                <div className="mb-3 d-flex justify-content-end">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="form-control form-control-sm me-2"
                    style={{ width: "200px" }}
                    value={searchTerm}
                    onChange={handleSearchInput}
                  />
                  <button className="btn btn-sm btn-secondary">
                    <FaSearch />
                  </button>

                  <button
                    className="btn btn-sm btn-primary ms-2"
                    onClick={handleAddBatch}
                  >
                    + Create Batch
                  </button>
                </div>

                <div className="card mb-4">
                  <div className="card-header">
                    <i className="fas fa-table me-1"></i> Batch Details
                  </div>
                  <div className="card-body shadow-sm border-0">
                    <DataTable
                      key={`${page}-${rowsPerPage}`}
                      className="data-table"
                      columns={columns}
                      data={batches}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 20, 50, 100]}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationDefaultPage={page}
                      highlightOnHover
                    />
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
